import { useMemo } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useAuth } from "../../../providers/AuthProvider";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";

import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { SectionWithHeader } from "../../shared/ui/SectionWithHeader";

import {
  ArrowLeft,
  ArrowRight,
  ClipboardText,
  MapTrifold,
  ChatCircleDots,
  SquaresFourIcon,
  UsersThree,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import { SurfaceCard } from "../../shared/ui/surface/SurfaceCard";

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const sectionStackClasses = "flex flex-col gap-6";
const heroTitleClasses = "text-5xl font-semibold text-slate-900 dark:text-slate-100";
const heroContentClasses = "max-w-3xl";
const featureGridClasses =
  "grid gap-6 md:grid-cols-2 xl:grid-cols-3";
const homeLinkClasses =
  "inline-flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300";
const ghostButtonClasses =
  "inline-flex items-center gap-2 rounded-md border border-transparent bg-transparent px-3 py-2 text-slate-600 font-semibold transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 dark:text-slate-400 dark:hover:bg-slate-800";
const statusBadgeBase =
  "inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold uppercase tracking-wide";
const plannedBadgeClasses =
  `${statusBadgeBase} border border-amber-200 bg-amber-100 text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/30 dark:text-amber-300`;
const actionPillClasses =
  "inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-600";
const comingSoonClasses =
  "inline-flex items-center rounded-md border border-dashed border-slate-300 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400";

type FeatureStatus = "available" | "planned";

interface AdminFeature {
  title: string;
  description: string;
  status: FeatureStatus;
  action?: {
    label: string;
    to: string;
    icon: ReactNode;
  };
}

export function AdminDashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const isAdmin = user?.system_role === "admin";

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [{ label: t("admin.control_center.title") }];
  }, [t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/dashboard" },
  });

  const featureGroups = useMemo(() => {
    const features: AdminFeature[] = [
      {
        title: t("admin.users.title"),
        description: t("admin.users.description"),
        status: "available",
        action: {
          label: t("admin.users.action"),
          to: "/admin/users",
          icon: <UsersThree {...defaultIconProps} aria-hidden />,
        },
      },
      {
        title: t("admin.tie_points.title"),
        description: t("admin.tie_points.description"),
        status: "available",
        action: {
          label: t("admin.tie_points.action"),
          to: "/admin/tie-points",
          icon: <MapTrifold {...defaultIconProps} aria-hidden />,
        },
      },
      {
        title: t("admin.audit_logs.title"),
        description: t("admin.audit_logs.description"),
        status: "available",
        action: {
          label: t("admin.audit_logs.action"),
          to: "/admin/audit-logs",
          icon: <ClipboardText {...defaultIconProps} aria-hidden />,
        },
      },
      {
        title: t("admin.billing.title"),
        description: t("admin.billing.description"),
        status: "planned",
      },
      {
        title: t("admin.support.title"),
        description: t("admin.support.description"),
        status: "available",
        action: {
          label: t("admin.support.action"),
          to: "/admin/support",
          icon: <ChatCircleDots {...defaultIconProps} aria-hidden />,
        },
      },
    ];

    return {
      available: features.filter((feature) => feature.status === "available"),
      planned: features.filter((feature) => feature.status === "planned"),
    };
  }, [t]);

  if (!user) {
    return null;
  }

  if (!isAdmin) {
    return (
      <PageShell className={pageStackClasses} width="wide">
        <PageHeader
          title={t("common.access_denied.title")}
          subtitle={t("common.access_denied.message")}
          contentClassName={heroContentClasses}
          titleClassName="text-4xl font-semibold text-slate-900 dark:text-slate-100"
          actions={
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleBack}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          }
        />
        <SurfaceCard
          tone="interactive"
          className="self-start"
          link={{ to: "/dashboard", label: t("navigation.to_dashboard") }}
        >
          <SurfaceCard.Body className={homeLinkClasses} gap="tight" textTone="muted">
            <SquaresFourIcon {...defaultIconProps} aria-hidden />
            {t("navigation.to_dashboard")}
          </SurfaceCard.Body>
        </SurfaceCard>
      </PageShell>
    );
  }

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />

      <PageHeader
        title={t("admin.control_center.title")}
        subtitle={t("admin.control_center.subtitle")}
        contentClassName={heroContentClasses}
        titleClassName={heroTitleClasses}
        actions={
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={handleBack}
          >
            <ArrowLeft {...defaultIconProps} aria-hidden />
            {t("actions.back")}
          </button>
        }
      />

      <SectionWithHeader
        className={sectionStackClasses}
        title={t("admin.available_now.title")}
        description={t("admin.available_now.description")}
      >
        <div className={featureGridClasses}>
          {featureGroups.available.map((feature) => (
            <SurfaceCard
              key={feature.title}
              className="flex flex-col gap-4"
              tone="interactive"
              link={
                feature.action
                  ? {
                      to: feature.action.to,
                      label: `${feature.title} — ${feature.action.label}`,
                    }
                  : undefined
              }
            >
              <SurfaceCard.Body gap="tight" textTone="muted">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.description}</p>
              </SurfaceCard.Body>
              {feature.action ? (
                <SurfaceCard.Footer className="mt-auto" justify="start">
                  <span className={actionPillClasses} aria-hidden="true">
                    {feature.action.icon}
                    {feature.action.label}
                    <ArrowRight {...defaultIconProps} aria-hidden />
                  </span>
                </SurfaceCard.Footer>
              ) : null}
            </SurfaceCard>
          ))}
        </div>
      </SectionWithHeader>

      <SectionWithHeader
        className={sectionStackClasses}
        title={t("admin.in_discovery.title")}
        description={t("admin.in_discovery.description")}
      >
        <div className={featureGridClasses}>
          {featureGroups.planned.map((feature) => (
            <SurfaceCard
              key={feature.title}
              className="flex flex-col gap-4"
              tone="muted"
            >
              <SurfaceCard.Body gap="tight" textTone="muted">
                <span className={plannedBadgeClasses}>
                  {t("common.status.planned")}
                </span>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.description}</p>
                <span className={comingSoonClasses}>
                  {t("common.status.coming_soon")}
                </span>
              </SurfaceCard.Body>
            </SurfaceCard>
          ))}
        </div>
      </SectionWithHeader>
    </PageShell>
  );
}
